import { useSelector } from "react-redux";
import { Response } from "react-select-async-paginate";
import { getCatalogItemList } from "../../api/catalog-items";
import CatalogItem from "../../models/catalog-item";
import { RootState } from "../../store";
import AsyncPaginateBootstrap from "../react-select-bootstrap/AsyncPaginateBootstrap";

const RECORDS_PER_SEARCH = 20;

type CatalogItemSelectProps = {
  value: CatalogItem | null;
  onChange: (_: CatalogItem | null) => void;
};

/**
 * Picks a product or service out of the tax payer's catalogue.
 *
 * An autocompletion and never a validation: the description of a line stays
 * free text, so finding nothing here is a normal outcome and the line is
 * invoiced exactly as it is today. Whoever has an empty catalogue sees an empty
 * list and carries on typing.
 */
const CatalogItemSelect: React.FC<CatalogItemSelectProps> = ({
  value,
  onChange,
}) => {
  const taxPayerId = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer.id,
  );

  const loadCatalogItemOptions = async (
    s: string,
    currentOpts: readonly (
      | CatalogItem
      | { options: CatalogItem[]; label?: string }
    )[],
  ): Promise<
    Response<CatalogItem, { options: CatalogItem[]; label?: string }, any>
  > => {
    const itemsPaginated = await getCatalogItemList(
      taxPayerId!,
      s,
      RECORDS_PER_SEARCH,
      currentOpts.length,
    );

    let newOpts: CatalogItem[] = [];
    let count = 0;

    if (itemsPaginated.data !== undefined) {
      newOpts = itemsPaginated.data.items;
      count = itemsPaginated.data.count;
    }

    return {
      options: newOpts,
      hasMore: currentOpts.length + newOpts.length < count,
    };
  };

  return (
    <AsyncPaginateBootstrap
      placeholder={"Buscar en productos y servicios (opcional)"}
      onChange={onChange}
      isMulti={false}
      isClearable={true}
      debounceTimeout={300}
      loadOptions={loadCatalogItemOptions}
      getOptionLabel={(option: CatalogItem) =>
        option.code
          ? `${option.code} - ${option.description}`
          : `${option.description}`
      }
      getOptionValue={(option: CatalogItem) => `${option.id}`}
      value={value}
      noOptionsMessage={() => "No hay productos ni servicios que coincidan"}
      loadingMessage={() => "Buscando..."}
    />
  );
};

export default CatalogItemSelect;
